/**
 * SED D3.3 — cross-source TRIANGULATION (agreement reward).
 *
 * The SED historically modelled only DISAGREEMENT (`evidenceConflict.ts`):
 * a value gets penalised when two sources conflict, but cross-source
 * AGREEMENT — the strongest positive trust signal — was unrewarded (the
 * sole exception being CAR-6's +0.05 boost for ≥2 agreeing auto-fill
 * providers). This module generalises that: per material slot it counts the
 * INDEPENDENT corroborating sources (provider + doc + report-claim +
 * authored), deduped by source FAMILY, and emits an agreement score.
 *
 * PURE + side-effect free + fully unit-testable. It reuses the same
 * `looksTriviallyEqual` comparator the disagreement detector uses, so the
 * agree/disagree decision shares ONE source of truth.
 *
 * SCORING: the rollup is OBSERVABILITY by default. It only feeds a small
 * capped confidence BONUS into `computeDataConfidencePenalty` when the
 * default-OFF `PORTFOLIO_CONFIG.ENABLE_D3_TRIANGULATION_SCORING` flag is
 * flipped on. Neutral (contributes 0) when the flag is off, so the
 * composite / confidenceTag / decisionBand are byte-identical until enabled.
 */
import { looksTriviallyEqual } from '../../services/evidenceConflict';
import type { EvidenceSection, SiteEvidenceDocument } from '../../types/siteEvidence';

/** One triangulation signal for a single (section, field) slot. */
export interface TriangulationSignal {
  sectionId: string;
  field: string;
  /** Independent corroborating source FAMILIES (deduped). */
  agreeingSources: string[];
  /** 0..1 — how strongly the independent sources agree on this slot. */
  agreementScore: number;
}

export interface TriangulationRollup {
  signals: TriangulationSignal[];
  /** 0..1 mean agreement across the emitted signals (0 when none). */
  meanAgreement: number;
}

/**
 * Source FAMILIES a slot can be corroborated by. We dedupe by family (not
 * by individual id) so two auto-fill providers from the same fleet, or a
 * provider value that also fed a doc extraction, count once per family.
 */
type SourceFamily = 'provider' | 'doc' | 'report' | 'authored';

/**
 * The flat scalar fields on a section's `data` payload (skips nested
 * objects + nullish — only comparable scalars participate in agreement).
 */
function flatScalars(data: unknown): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (!data || typeof data !== 'object') return out;
  for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
    if (v == null) continue;
    if (typeof v === 'object') continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    }
  }
  return out;
}

/**
 * Determine which source families corroborate a slot's resolved value,
 * each carrying a candidate value to compare. The PROVIDER family is the
 * resolved `slot.data`; DOC / REPORT / AUTHORED families are detected via
 * the provenance lineage the resolvers stamp.
 *
 * We deliberately keep this structural — we never re-fetch or re-extract;
 * we read the lineage the SED already carries. A family only counts when
 * the slot's provenance indicates it CONTRIBUTED (so a provider-only slot
 * is correctly a single-source slot, agreementScore 0).
 */
function corroboratingFamilies(slot: EvidenceSection<unknown>): Set<SourceFamily> {
  const families = new Set<SourceFamily>();
  // Provider family — present whenever the slot carries any data at all.
  if (slot.data != null) families.add('provider');

  const prov = slot.provenance;
  const detail = slot.provenanceDetail;

  // Doc family — a supporting doc fed or boosted the slot.
  if (
    prov === 'doc' ||
    prov === 'provider+doc' ||
    prov === 'doc-override' ||
    (slot.supportingDocIds && slot.supportingDocIds.length > 0)
  ) {
    families.add('doc');
  }

  // Report family — a prior generated report's finding contributed.
  if (
    prov === 'report-derived' ||
    prov === 'report+provider' ||
    prov === 'report-overrides-provider' ||
    detail?.derivedFromReport != null
  ) {
    families.add('report');
  }

  // Authored family — a user-authored claim fed the slot.
  if (
    slot.status === 'verified-from-context' ||
    (slot.authoredClaimIds && slot.authoredClaimIds.length > 0)
  ) {
    families.add('authored');
  }

  // CAR-6 — ≥2 independent auto-fill providers in agreement are themselves
  // a corroboration. We treat the auto-fill fleet as the PROVIDER family
  // (already added), so a multi-provider auto-fill does NOT double-count;
  // the family-level dedupe is the constitutional guard against inflating
  // agreement from same-family sources.
  return families;
}

/**
 * Map a provenance-detail lineage to the candidate values per family for a
 * given field, so we can run the SAME agreement comparator the conflict
 * detector uses. We compare against the resolved provider value.
 */
function familyCandidateValues(
  slot: EvidenceSection<unknown>,
  field: string,
): Partial<Record<SourceFamily, unknown>> {
  const out: Partial<Record<SourceFamily, unknown>> = {};
  const data = (slot.data as Record<string, unknown> | null) ?? null;
  if (data && Object.prototype.hasOwnProperty.call(data, field)) {
    out.provider = data[field];
  }
  const detail = slot.provenanceDetail;
  // The resolvers stamp providerValue / docValue on provenanceDetail when a
  // merge happened. docValue carries the doc OR report OR authored side
  // depending on which resolver ran (mergeReason discriminates).
  if (detail) {
    if (detail.providerValue !== undefined) out.provider = out.provider ?? detail.providerValue;
    if (detail.docValue !== undefined) {
      const reason = detail.mergeReason;
      if (reason === 'report-fills-gap' || reason === 'report-agreement' || reason === 'report-override') {
        out.report = detail.docValue;
      } else {
        out.doc = detail.docValue;
      }
    }
  }
  return out;
}

/**
 * Compute the triangulation rollup for a SED.
 *
 * For each section that carries data, for each scalar field, count the
 * independent corroborating source families whose candidate value
 * `looksTriviallyEqual` the resolved value. A signal is emitted ONLY for
 * MATERIAL slots — those with ≥2 independent families — so the rollup is
 * doc-size-bounded (the spec's SED-doc-size guard).
 *
 *   agreementScore = (#agreeing families) / (#contributing families)
 *
 * A single-source slot has no corroboration → no signal (agreementScore
 * would be the degenerate 1/1, which carries no triangulation information).
 *
 * `meanAgreement` is the mean `agreementScore` over the emitted signals,
 * or 0 when none. Deterministic: same SED → identical output.
 */
export function computeTriangulation(
  sed: Pick<SiteEvidenceDocument, never> & Record<string, unknown>,
): TriangulationRollup {
  const signals: TriangulationSignal[] = [];

  for (const [sectionId, raw] of Object.entries(sed)) {
    const slot = raw as EvidenceSection<unknown> | undefined;
    if (!slot || typeof slot !== 'object') continue;
    // Must look like an EvidenceSection with data.
    if (!('status' in slot) || !('source' in slot)) continue;
    if (slot.data == null) continue;

    const families = corroboratingFamilies(slot);
    // Need ≥2 families for triangulation to mean anything.
    if (families.size < 2) continue;

    const scalars = flatScalars(slot.data);
    for (const field of Object.keys(scalars)) {
      const candidates = familyCandidateValues(slot, field);
      const resolved = scalars[field];

      const agreeing: SourceFamily[] = [];
      const contributing: SourceFamily[] = [];
      for (const fam of families) {
        const candidate = candidates[fam];
        // A family only participates in THIS field's agreement test when it
        // carries a candidate value for the field. The provider family
        // always carries the resolved value.
        if (fam === 'provider') {
          contributing.push(fam);
          agreeing.push(fam); // resolved value agrees with itself
          continue;
        }
        if (candidate === undefined) continue;
        contributing.push(fam);
        if (looksTriviallyEqual(candidate, resolved)) agreeing.push(fam);
      }

      // Need ≥2 contributing families on THIS field for a meaningful signal.
      if (contributing.length < 2) continue;
      const agreementScore = agreeing.length / contributing.length;
      signals.push({
        sectionId,
        field,
        agreeingSources: agreeing.slice().sort(),
        agreementScore: Math.round(agreementScore * 1000) / 1000,
      });
    }
  }

  const meanAgreement =
    signals.length === 0
      ? 0
      : Math.round(
          (signals.reduce((acc, s) => acc + s.agreementScore, 0) / signals.length) * 1000,
        ) / 1000;

  return { signals, meanAgreement };
}
